<template>
  <div class="min-h-screen w-full">
    <!-- Hero Section -->
    <HeroSection 
      :queries="queries" 
      @search="handleHeroSearch" 
      @select-query="handleQuerySelect" 
    />

    <!-- Mobile Filter Button (only visible on small screens) -->
    <div class="md:hidden mb-4 sticky top-2 z-40 px-4">
      <button
        @click="showMobileFilters = !showMobileFilters"
        class="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#157813] text-white rounded-lg shadow-lg transition-transform hover:scale-105 focus:outline-none"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-5 w-5"
          viewBox="0 0 20 20"
          fill="#ffffff"
        >
          <path
            fill-rule="evenodd"
            d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 
            013 6V3z"
            clip-rule="evenodd"
          />
        </svg>
        Filters
      </button>
    </div>

    <!-- Main Container -->
    <div class="w-full px-4 py-6">
      <div class="flex flex-col md:flex-row gap-4">
        <!-- Filters Sidebar -->
        <aside
          class="md:w-72 w-full flex-shrink-0 bg-gray-100 rounded-lg shadow-sm border-gray-200 p-4 md:p-5 md:mr-6"
          :class="{
            'fixed inset-y-0 left-0 z-50 transform': true,
            '-translate-x-full': !showMobileFilters,
            'translate-x-0': showMobileFilters,
            'md:static md:translate-x-0': true,
          }"
        >
          <div class="md:hidden flex justify-between items-center mb-4 pb-2 border-b px-4 pt-4">
            <h3 class="text-lg font-bold text-gray-900">Filters</h3>
            <button
              @click="showMobileFilters = false"
              class="p-2 text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
              
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div class="flex justify-between items-center mb-4">
            <h3 class="font-semibold text-gray-900 text-2xl">Filters</h3>
            <div class="flex items-center gap-2 mx-2">
              <button
                @click="collapseAll"
                class="text-sm text-[#157813] hover:text-[#46cc44]"
                title="Collapse all"
              >
                ▲
              </button>
              <button
                @click="expandAll"
                class="text-sm text-[#157813] hover:text-[#46cc44]"
                title="Expand all"
              >
                ▼
              </button>
              <button
                @click="clearAllFilters"
                class="text-sm text-[#157813] hover:text-[#46cc44] font-medium flex items-center gap-1 whitespace-nowrap"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 
                    000 2v10a2 2 0 002 2h8a2 2 0 
                    002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 
                    0011 2H9zM7 8a1 1 0 
                    012 0v6a1 1 0 
                    11-2 0V8zm5-1a1 1 0 
                    00-1 1v6a1 1 0 
                    102 0V8a1 1 0 00-1-1z"
                    clip-rule="evenodd"
                  />
                </svg>
                Clear All
              </button>
            </div>
          </div>

          <div>
            <template v-for="(groupLabel, groupKey) in filterGroups" :key="groupKey">
              <div class="border-b border-gray-200 pb-5 mb-2">
                <div
                  class="flex justify-between items-center cursor-pointer hover:bg-gray-50 hover:rounded-md p-2 -m-2 transition-colors duration-200"
                  @click="toggleSection(groupKey)"
                >
                  <h4 class="text-sm font-medium text-gray-900">
                    {{ groupLabel }}
                    <span v-if="groupKey !== 'extraction' && getFilterOptionCount(groupKey) > 0" class="text-xs text-gray-500 ml-1">
                      ({{ getFilterOptionCount(groupKey) }})
                    </span>
                    <span v-if="groupKey !== 'extraction' && filters[groupKey] && filters[groupKey].length > 0" class="text-xs text-[#157813] ml-1">
                      ({{ filters[groupKey].length }})
                    </span>
                  </h4>
                  <span class="text-gray-500 text-sm">
                    {{ openSections[groupKey] ? "▼" : "▶" }}
                  </span>
                </div>

                <div v-show="openSections[groupKey]" class="mt-2 space-y-2">
                  <!-- Clear group button -->
                  <div class="flex justify-between items-center mb-2">
                    <button
                      @click.stop="clearFilterGroup(groupKey)"
                      class="text-xs text-[#157813] hover:text-[#46cc44] flex items-center gap-1"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-3 w-3"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M4.293 4.293a1 1 0 
                            011.414 0L10 8.586l4.293-4.293a1 1 0 
                            111.414 1.414L11.414 10l4.293 4.293a1 1 0 
                            01-1.414 1.414L10 11.414l-4.293 4.293
                            a1 1 0 01-1.414-1.414L8.586 10 
                            4.293 5.707a1 1 0 010-1.414z"
                          clip-rule="evenodd"
                        />
                      </svg>
                      Clear
                    </button>
                  </div>

                  <!-- SPECIAL CASE: Run Count range slider -->
                  <div v-if="groupKey === 'extraction'">
                    <div class="space-y-4 px-2">
                      <div v-if="filtersLoading || typeof minExtraction !== 'number' || typeof maxExtraction !== 'number' || minExtraction === maxExtraction" class="flex justify-center items-center py-4">
                        <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-[#157813]"></div>
                        <span class="ml-2 text-gray-600">Loading...</span>
                      </div>
                      <template v-else>
                        <!-- Header with current range -->
                        <div class="text-center">
                          <div class="text-sm font-medium text-gray-900 mb-1">
                            Run Count Range
                          </div>
                          <div class="text-xs text-gray-600">
                            Available: {{ minExtraction }} - {{ maxExtraction }} runs
                          </div>
                        </div>

                        <!-- Manual Input Fields -->
                        <div class="grid grid-cols-2 gap-3">
                          <div>
                            <label class="block text-xs font-medium text-gray-700 mb-1">Min Runs</label>
                            <input
                              type="number"
                              :min="minExtraction"
                              :max="maxExtraction"
                              :value="filters.extraction[0]"
                              @input="updateMinExtractionFromInput($event)"
                              class="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:border-[#157813] focus:ring-1 focus:ring-[#157813]"
                              placeholder="Min"
                            />
                          </div>
                          <div>
                            <label class="block text-xs font-medium text-gray-700 mb-1">Max Runs</label>
                            <input
                              type="number"
                              :min="minExtraction"
                              :max="maxExtraction"
                              :value="filters.extraction[1]"
                              @input="updateMaxExtractionFromInput($event)"
                              class="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:border-[#157813] focus:ring-1 focus:ring-[#157813]"
                              placeholder="Max"
                            />
                          </div>
                        </div>

                        <!-- Current Selection Display -->
                        <div class="bg-gray-50 rounded p-2 text-center">
                          <span class="text-sm text-gray-700">
                            Selected: <span class="font-medium text-[#157813]">{{ filters.extraction[0] }}</span> - 
                            <span class="font-medium text-[#157813]">{{ filters.extraction[1] }}</span> runs
                          </span>
                        </div>

                        <!-- Improved Visual Slider -->
                        <div class="space-y-2">
                          <div class="text-xs text-gray-600 text-center">Drag to adjust range</div>
                          <div class="slider-container">
                            <!-- Track -->
                            <div class="slider-track"></div>
                            <!-- Filled portion between handles -->
                            <div
                              class="slider-range"
                              :style="{
                                left: `${((filters.extraction[0] - minExtraction) / (maxExtraction - minExtraction)) * 100}%`,
                                width: `${((filters.extraction[1] - filters.extraction[0]) / (maxExtraction - minExtraction)) * 100}%`
                              }"
                            ></div>
                            <!-- Left handle (Min) -->
                            <input
                              type="range"
                              class="slider-thumb slider-thumb-min"
                              :min="minExtraction"
                              :max="maxExtraction"
                              step="1"
                              :value="filters.extraction[0]"
                              @input="updateMinExtraction($event)"
                              title="Minimum run count"
                            />
                            <!-- Right handle (Max) -->
                            <input
                              type="range"
                              class="slider-thumb slider-thumb-max"
                              :min="minExtraction"
                              :max="maxExtraction"
                              step="1"
                              :value="filters.extraction[1]"
                              @input="updateMaxExtraction($event)"
                              title="Maximum run count"
                            />
                          </div>
                          <!-- Min/Max labels -->
                          <div class="flex justify-between text-xs text-gray-500">
                            <span>{{ minExtraction }}</span>
                            <span>{{ maxExtraction }}</span>
                          </div>
                        </div>

                        <!-- Reset button -->
                        <div class="text-center">
                          <button
                            @click="resetExtractionRange"
                            class="text-xs text-[#157813] hover:text-[#46cc44] underline"
                          >
                            Reset to full range
                          </button>
                        </div>
                      </template>
                    </div>
                  </div>

                  <!-- DEFAULT CASE: multi-select checkboxes -->
                  <div v-else>
                    <div class="relative">
                      <div
                        class="dropdown-container cursor-pointer bg-white border border-gray-300 rounded-md p-3 text-sm flex items-center justify-between hover:border-green-500 hover:shadow-sm transition-all duration-200"
                        @click.stop="toggleDropdown(groupKey)"
                      >
                        <div class="flex flex-wrap gap-1 flex-1">
                          <!-- Display selected as chips -->
                          <!-- Special handling for queryType to show original types as tags -->
                          <template v-if="groupKey === 'queryType'">
                            <span
                              v-for="originalType in queryTypeTags"
                              :key="originalType"
                              class="bg-[#157813] text-white px-2 py-1 rounded-full text-xs flex items-center gap-1"
                            >
                              {{ originalType }}
                              <button
                                @click.stop="removeOriginalType(originalType)"
                                class="hover:text-emerald-200"
                              >
                                ×
                              </button>
                            </span>
                          </template>
                          <!-- Default handling for other filter types -->
                          <template v-else>
                            <span
                              v-for="selected in filters[groupKey]"
                              :key="selected"
                              class="bg-[#157813] text-white px-2 py-1 rounded-full text-xs flex items-center gap-1"
                            >
                              {{ selected }}
                              <button
                                @click.stop="
                                  filters[groupKey] = filters[groupKey].filter((f:any) => f !== selected)
                                "
                                class="hover:text-emerald-200"
                              >
                                ×
                              </button>
                            </span>
                          </template>
                          <!-- Placeholder input if no selection -->
                          <input
                            v-if="(groupKey === 'queryType' ? queryTypeTags.length === 0 : filters[groupKey].length === 0)"
                            type="text"
                            placeholder="Type to search fields..."
                            class="w-full outline-none bg-transparent placeholder-gray-400 text-gray-700"
                            @input="handleDropdownSearch(groupKey)"
                            @focus="toggleDropdown(groupKey)"
                            v-model="dropdownSearch[groupKey]"
                          />
                        </div>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          class="h-4 w-4 text-green-600 ml-2"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>

                      <!-- Popover for checkboxes -->
                      <div
                        v-show="dropdownStates[groupKey]"
                        class="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto dropdown-container"
                      >
                        <div class="p-2" @click.stop>
                          <div class="relative mb-2">
                            <input
                              type="text"
                              placeholder="Search..."
                              class="w-full pl-8 pr-2 py-1 border-b border-gray-200 focus:outline-none focus:border-[#46cc44]"
                              v-model="dropdownSearch[groupKey]"
                              @keydown.esc="dropdownStates[groupKey] = false"
                            />
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              class="h-4 w-4 absolute left-2 top-2 text-gray-400"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M21 21l-6-6m2-5a7 7 0 
                                  11-14 0 7 7 0 0114 0z"
                              />
                            </svg>
                          </div>

                          <div class="space-y-1">
                            <div
                              v-if="filteredOptions(groupKey).length === 0"
                              class="text-gray-500 text-xs italic px-2 py-1"
                            >
                              No options available
                            </div>
                            <label
                              v-else
                              v-for="option in filteredOptions(groupKey)"
                              :key="option"
                              class="flex items-center px-2 py-1 hover:bg-green-50 hover:text-green-700 cursor-pointer rounded transition-colors duration-150"
                            >
                              <input
                                type="checkbox"
                                :value="option"
                                v-model="filters[groupKey]"
                                class="mr-2 rounded border-gray-300"
                                style="accent-color: #157813"
                                @change="handleFilterChange(groupKey)"
                              />
                              <span class="text-sm text-gray-700">{{ option }}</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <!-- End default case -->
                </div>
              </div>
            </template>
          </div>
        </aside>

        <!-- Main Content -->
        <main class="flex-1 min-w-0">
          <!-- Loading State -->
          <div v-if="isLoading" class="py-8">
            <LoadingIndicator 
              title="Filtering queries..."
              subtitle="Applying your search criteria"
              status="Processing filters"
              :duration="500"
              :auto-progress="true"
            />
          </div>
          
          <!-- Results -->
          <div v-else>
            <div class="text-sm text-gray-600 mb-4 px-1">
              Showing {{ displayedQueries.length }} of {{ filteredQueries.length }} results
            </div>
            <div class="overflow-x-auto">
              <QueryTable
                :queries="displayedQueries"
                :headers="tableHeaders"
                :sort-state="sortState"
                @sort="sortBy"
                @row-click="goToDetails"
                @toggle-unique="handleToggleUnique"
              />
            </div>
          </div>

          <!-- Pagination -->
          <div v-if="filteredQueries.length > itemsPerPage" class="mt-6">
            <Pagination
              :current-page="currentPage"
              :total-items="filteredQueries.length"
              :items-per-page="itemsPerPage"
              @page-change="handlePageChange"
            />
          </div>

          <!-- Empty State -->
          <div
            v-if="!isLoading && displayedQueries.length === 0"
            class="text-center py-12 text-gray-500"
          >
            <svg
              class="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 
                  11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p class="mt-4">No queries found matching your criteria</p>
          </div>
        </main>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import HeroSection from "@/components/HeroSection.vue";
import LoadingIndicator from "@/components/LoadingIndicator.vue";
import Pagination from "@/components/Pagination.vue";
import QueryTable from "@/components/QueryTable.vue";
import SuggestionDropdown from "@/components/SuggestionDropdown.vue";
import type { Query } from "@/models/Query";
import { mapQueryType, transformQueries } from "@/models/Query";
import rawQueries from "@/queries.json";
import { clearFiltersFromStorage, loadFiltersFromStorage, saveFiltersToStorage, type FilterState } from "@/utils/filterPersistence";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useRouter } from "vue-router";

interface OpenSections {
  features: boolean;
  author: boolean;
  indicationEnrichments: boolean;
  medicationEnrichments: boolean;
  tableNames: boolean;
  columnNames: boolean;
  extraction: boolean;
  queryType: boolean;
}
type OpenSectionKey = keyof OpenSections;

export default {
  name: "YourComponentName",
  components: {
    HeroSection,
    QueryTable,
    LoadingIndicator,
    Pagination,
    SuggestionDropdown
  },
};
</script>

<script setup lang="ts">
const router = useRouter();

const queries = ref<Query[]>(transformQueries(rawQueries as any[]));
const filteredQueries = ref<Query[]>([]);
const displayedQueries = ref<Query[]>([]);
const itemsPerPage = 20;
const currentPage = ref(1);
const showUniqueQueries = ref(true);

// Filter state
const showMobileFilters = ref(false);
const isLoading = ref(false);
const filtersLoading = ref(false);
const filters = ref({
  features: [] as string[],
  indicationEnrichments: [] as string[],
  medicationEnrichments: [] as string[],
  tableNames: [] as string[],
  columnNames: [] as string[],
  author: [] as string[],
  extraction: [0, 0] as number[],
  queryType: [] as string[],
});

// Separate tracking for individual original query types
const selectedOriginalTypes = ref<string[]>([]);

const openSections = ref<OpenSections>({
  features: false,
  author: false,
  indicationEnrichments: false,
  medicationEnrichments: false,
  tableNames: false,
  columnNames: false,
  extraction: false,
  queryType: false,
});

const dropdownStates = ref<{ [key: string]: boolean }>({});
const dropdownSearch = ref<{ [key: string]: string }>({});
const heroSearchTerm = ref("");

const filterGroups = {
  queryType: "Query Types",
  features: "Features",
  author: "Authors",
  tableNames: "Table Names",
  columnNames: "Column Names",
  indicationEnrichments: "Indication Enrichments",
  medicationEnrichments: "Medication Enrichments",
  extraction: "Run Count",
};

const tableHeaders = [
  { key: "id", label: "Query ID" },
  { key: "neuronId", label: "Neuron ID" },
  { key: "neuronText", label: "SQL Query", searchable: true },
  { key: "author", label: "Author" },
  { key: "extraction", label: "Run Count" },
  { key: "queryStartTime", label: "Run Date" },
];

// Unique options
function getUniqueOptions(key: keyof Query) {
  return Array.from(new Set(queries.value.map((q) => q[key]))).filter(Boolean).sort() as string[];
}
function getUniqueArrayOptions(key: keyof Query) {
  return Array.from(new Set(queries.value.flatMap((q) => q[key] || [])))
    .filter(Boolean)
    .sort() as string[];
}

// Helper function to get original types from mapped type
function getOriginalTypesFromMapped(mappedType: string): string[] {
  if (mappedType === "Original Query") {
    return ["MAIN"];
  } else if (mappedType === "Extracted Neuron") {
    return ["CTE", "FROM", "JOIN"];
  }
  return [];
}

// Helper function to get mapped type from original type
function getMappedTypeFromOriginal(originalType: string): string {
  return mapQueryType(originalType);
}

// Function to remove an original type
function removeOriginalType(originalTypeToRemove: string) {
  // Remove the specific original type
  selectedOriginalTypes.value = selectedOriginalTypes.value.filter(type => type !== originalTypeToRemove);
  
  // Update the mapped types based on remaining original types
  updateMappedTypesFromOriginal();
}

// Function to update mapped types based on selected original types
function updateMappedTypesFromOriginal() {
  const mappedTypes = new Set<string>();
  
  selectedOriginalTypes.value.forEach(originalType => {
    const mappedType = getMappedTypeFromOriginal(originalType);
    mappedTypes.add(mappedType);
  });
  
  filters.value.queryType = Array.from(mappedTypes);
}

// Get filter option count
function getFilterOptionCount(groupKey: string): number {
  if (groupKey === "extraction") {
    return maxExtraction.value - minExtraction.value + 1;
  } else if (groupKey === "queryType") {
    return 2; // "Extracted Neuron" and "Original Query"
  } else {
    const options = filterOptions.value[groupKey as keyof typeof filterOptions.value];
    return Array.isArray(options) ? options.length : 0;
  }
}



const filterOptions = computed(() => ({
  features: getUniqueArrayOptions("features"),
  indicationEnrichments: getUniqueArrayOptions("indicationEnrichments"),
  medicationEnrichments: getUniqueArrayOptions("medicationEnrichments"),
  tableNames: getUniqueArrayOptions("tableNames"),
  columnNames: getUniqueArrayOptions("columnNames"),
  author: getUniqueOptions("author"),
  queryType: ["Extracted Neuron", "Original Query"], // Show mapped names
}));

// Computed property to get original types as tags
const queryTypeTags = computed(() => {
  return selectedOriginalTypes.value;
});

// Run count min/max
const minExtraction = computed(() => {
  if (queries.value.length === 0) return 0;
  return Math.min(...queries.value.map((q) => q.extraction ?? 0));
});
const maxExtraction = computed(() => {
  if (queries.value.length === 0) return 100;
  return Math.max(...queries.value.map((q) => q.extraction ?? 0));
});

// Searching & sorting
function searchInObject(obj: any, text: string): boolean {
  if (!text) return true;
  const lower = text.toLowerCase();
  return Object.values(obj).some((value) => {
    if (typeof value === "string") return value.toLowerCase().includes(lower);
    if (Array.isArray(value)) return value.some((v: string) => v.toLowerCase().includes(lower));
    if (value && typeof value === "object") return searchInObject(value, text);
    return false;
  });
}

const sortState = ref<{ key: string; direction: "asc" | "desc" }>({
  key: "id",
  direction: "asc",
});
function sortBy(columnKey: string) {
  if (sortState.value.key === columnKey) {
    sortState.value.direction = sortState.value.direction === "asc" ? "desc" : "asc";
  } else {
    sortState.value.key = columnKey;
    if (columnKey === "extraction") {
      sortState.value.direction = "desc";
    } else if (columnKey === "id") {
      sortState.value.direction = "asc";
    } else {
      sortState.value.direction = "asc";
    }
  }
}
function compareQueries(a: Query, b: Query) {
  const { key, direction } = sortState.value;
  const valA = a[key as keyof Query];
  const valB = b[key as keyof Query];

  if (key === "id") {
    // Sort by ID as string, but handle numeric parts properly
    const aStr = String(valA || "");
    const bStr = String(valB || "");
    return direction === "asc"
      ? aStr.localeCompare(bStr, undefined, { numeric: true })
      : bStr.localeCompare(aStr, undefined, { numeric: true });
  } else if (key === "neuronId") {
    // Extract numbers after AST_ for comparison
    const numA = parseInt((String(valA).split('AST_')[1] || '0'), 10);
    const numB = parseInt((String(valB).split('AST_')[1] || '0'), 10);
    return direction === "asc" ? numA - numB : numB - numA;
  } else if (key === "extraction") {
    const numA = Number(valA ?? 0);
    const numB = Number(valB ?? 0);
    return direction === "asc" ? numA - numB : numB - numA;
  } else {
    const sA = String(valA ?? "");
    const sB = String(valB ?? "");
    const cmp = sA.localeCompare(sB);
    return direction === "asc" ? cmp : -cmp;
  }
}

function applyFilters() {
  isLoading.value = true;
  
  try {
    const results = queries.value.filter((q) => {
      const textMatch = searchInObject(q, heroSearchTerm.value);

      // Handle author filter
      const authorMatch = filters.value.author.length === 0 || 
        filters.value.author.includes(q.author);

      // Handle query type filter
      const queryTypeMatch = selectedOriginalTypes.value.length === 0 || 
        selectedOriginalTypes.value.includes(q.componentType);

      // Handle everything else
      const otherFiltersMatch = Object.entries(filters.value)
        .filter(([k]) => k !== "author" && k !== "queryType")
        .every(([key, values]) => {
          if (!values || values.length === 0) return true;
          if (key === "extraction") {
            const [minVal, maxVal] = filters.value.extraction;
            const numericVal = q.extraction ?? 0;
            return numericVal >= minVal && numericVal <= maxVal;
          } else {
            const filterValues = values as string[];
            const queryValue = q[key as keyof Query];
            if (Array.isArray(queryValue)) {
              return filterValues.some((v) => queryValue.map(String).includes(v));
            } else {
              return filterValues.includes(String(queryValue));
            }
          }
        });

      return textMatch && authorMatch && queryTypeMatch && otherFiltersMatch;
    });

    // Apply unique filter if enabled
    if (showUniqueQueries.value) {
      const uniqueMap = new Map();
      results.forEach((query) => {
        if (!uniqueMap.has(query.neuronId)) {
          uniqueMap.set(query.neuronId, query);
        }
      });
      filteredQueries.value = Array.from(uniqueMap.values());
    } else {
      filteredQueries.value = results;
    }
    
    // Sort the filtered results
    filteredQueries.value.sort(compareQueries);
    
    // Reset to first page when filters change
    currentPage.value = 1;
    updateDisplayedQueries();
  } catch (error) {
    console.error('Error in applyFilters:', error);
    // Fallback: show all queries if filtering fails
    filteredQueries.value = queries.value;
    currentPage.value = 1;
    updateDisplayedQueries();
  } finally {
    isLoading.value = false;
  }
}

// Range slider updates
function updateMinExtraction(e: Event) {
  const val = parseInt((e.target as HTMLInputElement).value, 10);
  filters.value.extraction[0] = val;
  if (val > filters.value.extraction[1]) {
    filters.value.extraction[1] = val;
  }
}
function updateMaxExtraction(e: Event) {
  const val = parseInt((e.target as HTMLInputElement).value, 10);
  filters.value.extraction[1] = val;
  if (val < filters.value.extraction[0]) {
    filters.value.extraction[0] = val;
  }
}

// Input field updates
function updateMinExtractionFromInput(e: Event) {
  const val = parseInt((e.target as HTMLInputElement).value, 10);
  if (isNaN(val)) return;
  
  const minVal = Math.max(minExtraction.value, val);
  const maxVal = Math.min(maxExtraction.value, val);
  
  filters.value.extraction[0] = minVal;
  if (minVal > filters.value.extraction[1]) {
    filters.value.extraction[1] = minVal;
  }
}

function updateMaxExtractionFromInput(e: Event) {
  const val = parseInt((e.target as HTMLInputElement).value, 10);
  if (isNaN(val)) return;
  
  const minVal = Math.max(minExtraction.value, val);
  const maxVal = Math.min(maxExtraction.value, val);
  
  filters.value.extraction[1] = maxVal;
  if (maxVal < filters.value.extraction[0]) {
    filters.value.extraction[0] = maxVal;
  }
}

// Reset extraction range to full range
function resetExtractionRange() {
  filters.value.extraction = [minExtraction.value, maxExtraction.value];
}

// Expand/collapse
function toggleSection(section: OpenSectionKey) {
  openSections.value[section] = !openSections.value[section];
}
function expandAll() {
  (Object.keys(openSections.value) as OpenSectionKey[]).forEach((key) => {
    openSections.value[key] = true;
  });
}
function collapseAll() {
  (Object.keys(openSections.value) as OpenSectionKey[]).forEach((key) => {
    openSections.value[key] = false;
  });
}

// Dropdown
function toggleDropdown(groupKey: string) {
  dropdownStates.value[groupKey] = !dropdownStates.value[groupKey];
}

function handleDropdownSearch(groupKey: string) {
  // This function handles the input event for dropdown search
  // The actual filtering is handled by the filteredOptions function
  // We can add additional logic here if needed
}

function filteredOptions(groupKey: string) {
  const options = filterOptions.value[groupKey as keyof typeof filterOptions.value] || [];
  const searchTerm = dropdownSearch.value[groupKey]?.toLowerCase() || "";
  return options.filter((o) => o.toLowerCase().includes(searchTerm));
}

// Handle filter changes from dropdowns
function handleFilterChange(groupKey: string) {
  // Special handling for queryType
  if (groupKey === 'queryType') {
    updateOriginalTypesFromMapped();
  }
  
  // Close dropdown after selection
  setTimeout(() => {
    dropdownStates.value[groupKey] = false;
  }, 100);
}

// Function to update original types based on selected mapped types
function updateOriginalTypesFromMapped() {
  const originalTypes = new Set<string>();
  
  filters.value.queryType.forEach(mappedType => {
    const types = getOriginalTypesFromMapped(mappedType);
    types.forEach(type => originalTypes.add(type));
  });
  
  selectedOriginalTypes.value = Array.from(originalTypes);
}

// Clear filters
function clearFilterGroup(groupKey: string) {
  if (groupKey === "extraction") {
    filters.value.extraction = [minExtraction.value, maxExtraction.value];
  } else if (groupKey === "queryType") {
    filters.value.queryType = [];
    selectedOriginalTypes.value = [];
    dropdownSearch.value[groupKey] = "";
  } else {
    filters.value[groupKey as keyof typeof filters.value] = [];
    dropdownSearch.value[groupKey] = "";
  }
}
function clearAllFilters() {
  Object.keys(filters.value).forEach((k) => {
    if (k === "extraction") {
      filters.value.extraction = [minExtraction.value, maxExtraction.value];
    } else {
      filters.value[k as keyof typeof filters.value] = [];
    }
  });
  selectedOriginalTypes.value = []; // Clear original types
  Object.keys(dropdownSearch.value).forEach((k) => {
    dropdownSearch.value[k] = "";
  });
  heroSearchTerm.value = "";
  
  // Clear from localStorage as well
  clearFiltersFromStorage();
}

// Filter persistence functions
function saveCurrentFilters() {
  const filterState: FilterState = {
    searchTerm: heroSearchTerm.value,
    selectedFeatures: filters.value.features,
    selectedTags: [], // Not used in this view
    selectedAuthors: filters.value.author,
    selectedQueryTypes: selectedOriginalTypes.value,
    selectedTables: filters.value.tableNames,
    selectedColumns: filters.value.columnNames,
    selectedIndicationEnrichments: filters.value.indicationEnrichments,
    selectedMedicationEnrichments: filters.value.medicationEnrichments,
    extractionRange: filters.value.extraction as [number, number],
    isExtractionRangeSet: filters.value.extraction[0] !== 0 || filters.value.extraction[1] !== 0,
    showUniqueQueries: showUniqueQueries.value,
  };
  saveFiltersToStorage(filterState);
}

function loadPersistedFilters() {
  const persisted = loadFiltersFromStorage();
  if (persisted) {
    if (persisted.searchTerm) heroSearchTerm.value = persisted.searchTerm;
    if (persisted.selectedFeatures) filters.value.features = persisted.selectedFeatures;
    if (persisted.selectedAuthors) filters.value.author = persisted.selectedAuthors;
    if (persisted.selectedQueryTypes) selectedOriginalTypes.value = persisted.selectedQueryTypes;
    if (persisted.selectedTables) filters.value.tableNames = persisted.selectedTables;
    if (persisted.selectedColumns) filters.value.columnNames = persisted.selectedColumns;
    if (persisted.selectedIndicationEnrichments) filters.value.indicationEnrichments = persisted.selectedIndicationEnrichments;
    if (persisted.selectedMedicationEnrichments) filters.value.medicationEnrichments = persisted.selectedMedicationEnrichments;
    if (persisted.extractionRange) filters.value.extraction = persisted.extractionRange;
    if (persisted.showUniqueQueries !== undefined) showUniqueQueries.value = persisted.showUniqueQueries;
  }
}

// Navigation
function goToDetails(queryId: string) {
  // Navigate to query details in the same tab
  router.push(`/query/${queryId}`);
}
function handleHeroSearch(term: string) {
  console.log('Hero search received:', term);
  heroSearchTerm.value = term;
  currentPage.value = 1;
  applyFilters();
}

function handleQuerySelect(query: Query) {
  // Navigate to the query details page
  goToDetails(query.id);
}

// Handle page change
function handlePageChange(page: number) {
  currentPage.value = page;
  updateDisplayedQueries();
}

// Add function to update displayed queries
function updateDisplayedQueries() {
  const startIndex = (currentPage.value - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  displayedQueries.value = filteredQueries.value.slice(startIndex, endIndex);
}

// Add handleToggleUnique function
const handleToggleUnique = (show: boolean) => {
  showUniqueQueries.value = show;
  applyFilters();
};

// Simple debounced apply filters for dropdown interactions
let filterDebounceTimeout: number | undefined;
function debouncedApplyFilters() {
  if (filterDebounceTimeout) {
    clearTimeout(filterDebounceTimeout);
  }
  filterDebounceTimeout = window.setTimeout(() => {
    applyFilters();
  }, 150);
}

// Watch & Lifecycle
watch([heroSearchTerm, filters, sortState], () => {
  currentPage.value = 1; // Reset to first page when filters change
  applyFilters();
}, { deep: true });

// Watch for filter changes and save to localStorage
watch([heroSearchTerm, filters, showUniqueQueries, selectedOriginalTypes], () => {
  saveCurrentFilters();
}, { deep: true });

onMounted(() => {
  document.addEventListener("click", closeDropdowns);
  
  // Load persisted filters first
  loadPersistedFilters();
  
  // Set extraction range if not loaded from persistence
  if (filters.value.extraction[0] === 0 && filters.value.extraction[1] === 0) {
    filters.value.extraction = [minExtraction.value, maxExtraction.value];
  }
  
  // Set queryType section to be open by default
  openSections.value.queryType = false;
  currentPage.value = 1;
  
  // Load data immediately
  applyFilters();
});
onUnmounted(() => {
  document.removeEventListener("click", closeDropdowns);
});
function closeDropdowns(e: MouseEvent) {
  const t = e.target as HTMLElement;
  if (!t.closest(".dropdown-container")) {
    Object.keys(dropdownStates.value).forEach((k) => {
      dropdownStates.value[k] = false;
    });
  }
}

// Expose (optional)
defineExpose({
  filteredQueries,
  displayedQueries,
  heroSearchTerm,
  filters,
  openSections,
  dropdownStates,
  dropdownSearch,
  sortState,
  sortBy,
});
</script>

<style scoped>
/* Custom scrollbar styles */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}
::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}
::-webkit-scrollbar-thumb {
  background: #157813;
  border-radius: 4px;
}
::-webkit-scrollbar-thumb:hover {
  background: #46cc44;
}

/* 
  ============================
  DUAL RANGE SLIDER
  ============================
*/

/* Container for the track & both thumbs */
.slider-container {
  position: relative;
  height: 40px; /* Enough space for big thumbs */
}

/* Gray track behind both thumbs */
.slider-track {
  position: absolute;
  top: 50%;
  left: 0; right: 0;
  transform: translateY(-50%);
  height: 8px;
  background: #ccc;
  border-radius: 4px;
}

/* Filled portion between the min & max thumbs */
.slider-range {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  height: 8px;
  background: #157813;
  border-radius: 4px;
}

/* The actual range inputs (two of them) */
.slider-thumb {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  width: 100%;
  appearance: none;
  background: transparent;
  cursor: pointer;
}

/* Minimal track style for each input (we'll see the big track behind it) */
.slider-thumb::-webkit-slider-runnable-track {
  height: 8px;
  background: transparent;
}
.slider-thumb::-moz-range-track {
  height: 8px;
  background: transparent;
}

/* THUMB circle styling */
.slider-thumb::-webkit-slider-thumb {
  -webkit-appearance: none;
  height: 28px;
  width: 28px;
  margin-top: -10px; /* centers the circle on an 8px track */
  background: #157813;
  border-radius: 50%;
  border: 3px solid white;
  box-shadow: 0 1px 4px rgba(0,0,0,0.3);
  cursor: pointer;
}
.slider-thumb::-moz-range-thumb {
  height: 28px;
  width: 28px;
  background: #157813;
  border-radius: 50%;
  border: 3px solid white;
  box-shadow: 0 1px 4px rgba(0,0,0,0.3);
  cursor: pointer;
}

/* The left (min) input can only be clicked in the left half of the container */
.slider-thumb-min {
  z-index: 3;
  clip-path: inset(0 50% 0 0); 
}

/* The right (max) input can only be clicked in the right half */
.slider-thumb-max {
  z-index: 4;
  clip-path: inset(0 0 0 50%);
}

/* Hover effect for thumbs */
.slider-thumb::-webkit-slider-thumb:hover {
  transform: scale(1.1);
}
.slider-thumb::-moz-range-thumb:hover {
  transform: scale(1.1);
}
</style>
